import type { InboxPullRequest } from '@/app/(home)/inbox/types';
import type { AutoFixLoopPresentation } from '@/lib/auto-fix-loop/presentation';

import { describeCubicWork } from '@/app/(home)/inbox/utils/cubic-reviewer-presentation';

/** Someone on a pull request, as an avatar and a login show them. */
export type PullRequestPerson = { login: string; avatarUrl: string | null };

type InboxReview = InboxPullRequest['reviews'][number];

function toPerson(review: InboxReview | undefined): PullRequestPerson | null {
  return review?.user ? { login: review.user.login, avatarUrl: review.user.avatar_url } : null;
}

function newestFirst(reviews: InboxReview[]): InboxReview[] {
  return [...reviews].sort((a, b) => (b.submitted_at ?? '').localeCompare(a.submitted_at ?? ''));
}

/**
 * Whether someone other than the author wrote the review. The inbox already leaves bot reviews out;
 * the author's own go too, because GitHub records the author's reply to a review thread as a review
 * of theirs.
 */
export function isReviewByOther(pr: InboxPullRequest, review: InboxReview): boolean {
  return Boolean(review.user && review.user.login.toLowerCase() !== pr.author.toLowerCase());
}

/** Other people's reviews, newest first. */
function getReviewsByOthers(pr: InboxPullRequest): InboxReview[] {
  return newestFirst((pr.reviews ?? []).filter((review) => isReviewByOther(pr, review)));
}

/** Review states that replace a reviewer's earlier verdict; a comment leaves it standing. */
const VERDICT_STATES = new Set(['APPROVED', 'CHANGES_REQUESTED', 'DISMISSED']);

/** Someone's standing verdict on the pull request: their latest approval, change request or dismissal. */
export function getLatestVerdictBy(pr: InboxPullRequest, login: string): string | null {
  const reviewer = login.toLowerCase();
  const verdict = newestFirst(pr.reviews ?? []).find(
    (review) => review.user?.login.toLowerCase() === reviewer && VERDICT_STATES.has(review.state),
  );
  return verdict?.state ?? null;
}

/** Whoever last approved the pull request, or last requested changes on it. */
export function getLatestReviewer(
  pr: InboxPullRequest,
  state: 'APPROVED' | 'CHANGES_REQUESTED',
): PullRequestPerson | null {
  return toPerson(getReviewsByOthers(pr).find((review) => review.state === state));
}

/**
 * A requested person, else a requested team, else whoever reviewed last. Null while nobody besides
 * the author is on the pull request.
 */
export function getFirstReviewer(pr: InboxPullRequest): PullRequestPerson | null {
  const requestedReviewer = pr.requested_reviewers?.find((reviewer) => reviewer?.login);
  if (requestedReviewer) {
    return { login: requestedReviewer.login, avatarUrl: requestedReviewer.avatar_url };
  }
  const requestedTeam = pr.requested_teams?.[0];
  if (requestedTeam) {
    return { login: requestedTeam.name, avatarUrl: requestedTeam.avatar_url };
  }
  return toPerson(getReviewsByOthers(pr)[0]);
}

/** Nobody is on the pull request: no reviewer besides the author, and cubic is not working on it. */
export function isNobodyOn(pr: InboxPullRequest, autoFix: AutoFixLoopPresentation | null): boolean {
  return !getFirstReviewer(pr) && !describeCubicWork(pr.latestCubicReview, autoFix);
}
